import { borderRadius, colors, fontSizes, fontWeights } from '@/theme'
import { LinkOutlined, Merge, Reply, Warning } from '@mui/icons-material'
import CloseIcon from '@mui/icons-material/Close'
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material'

function NumberedStep({ number, title, subtitle }: { number: number; title: string; subtitle?: string }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
      <Box
        sx={{
          width: 24,
          height: 24,
          borderRadius: borderRadius.full,
          bgcolor: colors.brand.primary,
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          fontSize: fontSizes.sm,
          fontWeight: fontWeights.bold,
          mt: 0.25
        }}
      >
        {number}
      </Box>
      <Box>
        <Typography sx={{ fontSize: fontSizes.base, fontWeight: fontWeights.medium }}>{title}</Typography>
        {subtitle && <Typography sx={{ fontSize: fontSizes.sm, color: colors.text.secondary }}>{subtitle}</Typography>}
      </Box>
    </Box>
  )
}

function WarningBanner({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        display: 'flex',
        gap: 1.5,
        p: 2,
        bgcolor: colors.highlight.bg,
        border: `1px solid ${colors.highlight.border}`,
        borderRadius: borderRadius.sm,
        mb: 3
      }}
    >
      <Warning sx={{ color: colors.status.warning, fontSize: 20, mt: 0.25 }} />
      <Box>{children}</Box>
    </Box>
  )
}

function DialogHeader({
  icon,
  title,
  subtitle,
  onClose
}: {
  icon?: React.ReactNode
  title: string
  subtitle?: string
  onClose: () => void
}) {
  return (
    <DialogTitle sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', pb: 1 }}>
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {icon}
          <Typography sx={{ fontSize: fontSizes.xl, fontWeight: fontWeights.bold }}>{title}</Typography>
        </Box>
        {subtitle && (
          <Typography sx={{ fontSize: fontSizes.sm, color: colors.text.secondary, mt: 0.5 }}>{subtitle}</Typography>
        )}
      </Box>
      <IconButton onClick={onClose} size='small' sx={{ mt: -0.5 }}>
        <CloseIcon />
      </IconButton>
    </DialogTitle>
  )
}

const dialogPaperProps = { sx: { borderRadius: borderRadius.md } }

interface ConfirmDialogProps {
  open: boolean
  onConfirm: () => void
  onCancel: () => void
  isLoading?: boolean
}

export function MergeConfirmDialog({
  open,
  patientCount,
  changes,
  onConfirm,
  onCancel,
  isLoading
}: ConfirmDialogProps & { patientCount: number; changes?: { label: string; value: string | null }[] }) {
  return (
    <Dialog
      open={open}
      onClose={isLoading ? undefined : onCancel}
      maxWidth='sm'
      fullWidth
      PaperProps={dialogPaperProps}
    >
      <DialogHeader
        icon={<Merge sx={{ color: colors.brand.primary }} />}
        title={`Confirm patient merge ( ${patientCount} patients)`}
        onClose={onCancel}
      />
      <DialogContent>
        <WarningBanner>
          <Typography sx={{ fontSize: fontSizes.base, fontWeight: fontWeights.bold, mb: 0.5 }}>Important</Typography>
          <Typography sx={{ fontSize: fontSizes.sm }}>
            Please ensure you have selected the correct details for the merged patient. All selected patient data will
            be combined into a single record, and the merged patient will retain their verification and red flag status.
            This cannot be undone.
          </Typography>
        </WarningBanner>

        {changes && changes.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography sx={{ fontSize: fontSizes.base, fontWeight: fontWeights.bold, mb: 1 }}>
              Values you are setting
            </Typography>
            <Typography sx={{ fontSize: fontSizes.sm, color: colors.text.secondary, mb: 1 }}>
              Everything else is worked out by the API from the two records.
            </Typography>
            <Box component='ul' sx={{ m: 0, pl: 2.5, listStyleType: 'disc' }}>
              {changes.map(change => (
                <Box component='li' key={change.label} sx={{ py: 0.25 }}>
                  <Typography sx={{ fontSize: fontSizes.sm }}>
                    <strong>{change.label}:</strong> {change.value ?? 'None'}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        <Typography sx={{ fontSize: fontSizes.base, fontWeight: fontWeights.bold, mb: 2 }}>
          What happens next
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <NumberedStep
            number={1}
            title='Patient records will be merged'
            subtitle='The patients details will be merged but will retain the verification and red flag status'
          />
          <NumberedStep number={2} title="The resulting merged patient will be visible within the user's account" />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button variant='ghost' onClick={onCancel} disabled={isLoading}>
          Cancel
        </Button>
        <Button variant='primary' startIcon={<Merge />} onClick={onConfirm} disabled={isLoading} loading={isLoading}>
          Merge Patients
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export function DontMergeDialog({ open, onConfirm, onCancel }: ConfirmDialogProps) {
  return (
    <Dialog open={open} onClose={onCancel} maxWidth='sm' fullWidth PaperProps={dialogPaperProps}>
      <DialogHeader title="Don't merge these patients" onClose={onCancel} />
      <DialogContent>
        <WarningBanner>
          <Typography sx={{ fontSize: fontSizes.base, fontWeight: fontWeights.bold, mb: 0.5 }}>Important</Typography>
          <Typography sx={{ fontSize: fontSizes.sm }}>
            By confirming that these patients are not duplicates, they will be removed from the review queue and won't
            be suggested for merging again. Please ensure the non-matching fields (highlighted in yellow) confirm that
            these are genuinely different patients.
          </Typography>
        </WarningBanner>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button variant='ghost' onClick={onCancel}>
          Cancel
        </Button>
        <Button variant='primary' onClick={onConfirm}>
          Confirm, not duplicates
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export function DeferDialog({ open, onConfirm, onCancel }: ConfirmDialogProps) {
  return (
    <Dialog open={open} onClose={onCancel} maxWidth='sm' fullWidth PaperProps={dialogPaperProps}>
      <DialogHeader title='Defer this decision' onClose={onCancel} />
      <DialogContent>
        <WarningBanner>
          <Typography sx={{ fontSize: fontSizes.base, fontWeight: fontWeights.bold, mb: 0.5 }}>Important</Typography>
          <Typography sx={{ fontSize: fontSizes.sm }}>
            You can defer this request if you're unsure about merging these patient records. The request will be sent to
            the Deferred requests queue and can be reviewed later.
          </Typography>
        </WarningBanner>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button variant='ghost' onClick={onCancel}>
          Cancel
        </Button>
        <Button variant='primary' startIcon={<Reply />} onClick={onConfirm}>
          Defer decision
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export function KeepSeparateDialog({
  open,
  patientNumber,
  onConfirm,
  onCancel
}: ConfirmDialogProps & { patientNumber: number }) {
  return (
    <Dialog open={open} onClose={onCancel} maxWidth='sm' fullWidth PaperProps={dialogPaperProps}>
      <DialogHeader
        title={`Keep Patient ${patientNumber} as a separate patient`}
        subtitle='Confirm this patient is not a duplicate'
        onClose={onCancel}
      />
      <DialogContent>
        <WarningBanner>
          <Typography sx={{ fontSize: fontSizes.base, fontWeight: fontWeights.bold, mb: 0.5 }}>Important</Typography>
          <Typography sx={{ fontSize: fontSizes.sm }}>
            Keep this patient separate if you've confirmed they are not a duplicate. This will ensure it remains an
            individual patient record.
          </Typography>
        </WarningBanner>

        <Typography sx={{ fontSize: fontSizes.base, fontWeight: fontWeights.bold, mb: 2 }}>
          What happens next
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <NumberedStep
            number={1}
            title='This patient will remain as a separate record'
            subtitle="No changes will be made to this patient's information"
          />
          <NumberedStep number={2} title='The remaining patients can still be reviewed to be merged' />
          <NumberedStep
            number={3}
            title='Any field selections from this patient will be cleared'
            subtitle='You can only select values from patients marked for merging'
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button variant='ghost' onClick={onCancel}>
          Cancel
        </Button>
        <Button variant='primary' onClick={onConfirm}>
          Remove Patient
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export function LinkConfirmDialog({ open, onConfirm, onCancel }: ConfirmDialogProps) {
  return (
    <Dialog open={open} onClose={onCancel} maxWidth='sm' fullWidth PaperProps={dialogPaperProps}>
      <DialogHeader
        title='Link patient records'
        subtitle='Please ensure you have reviewed the records before linking these patients'
        onClose={onCancel}
      />
      <DialogContent>
        <WarningBanner>
          <Typography sx={{ fontSize: fontSizes.base, fontWeight: fontWeights.bold, mb: 0.5 }}>Important</Typography>
          <Typography sx={{ fontSize: fontSizes.sm }}>
            By linking a patient record CS agents and prescribers can view the patient&apos;s full medical request
            history across the patient records. This improves visibility and better healthcare decision making. The link
            can be reversed at any time without affecting the data.
          </Typography>
        </WarningBanner>

        <Typography sx={{ fontSize: fontSizes.base, fontWeight: fontWeights.bold, mb: 2 }}>
          What happens next
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <NumberedStep
            number={1}
            title='Patient records stay separate in their own user accounts'
            subtitle='All medication requests stay exactly where they are - nothing moves'
          />
          <NumberedStep
            number={2}
            title='CS agents can view combined order history for better support'
            subtitle="This allows for better support as you'll have a full view of the patients requests across both users"
          />
          <NumberedStep number={3} title='This link is internal only and not visible to users' />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button variant='ghost' onClick={onCancel}>
          Cancel
        </Button>
        <Button variant='primary' startIcon={<LinkOutlined />} onClick={onConfirm}>
          Link patients
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export function DontLinkDialog({ open, onConfirm, onCancel }: ConfirmDialogProps) {
  return (
    <Dialog open={open} onClose={onCancel} maxWidth='sm' fullWidth PaperProps={dialogPaperProps}>
      <DialogHeader title="Don't link patients" onClose={onCancel} />
      <DialogContent>
        <WarningBanner>
          <Typography sx={{ fontSize: fontSizes.base, fontWeight: fontWeights.bold, mb: 0.5 }}>Important</Typography>
          <Typography sx={{ fontSize: fontSizes.sm }}>
            These patient records will not be linked. CS agents won&apos;t be able to view their records or requests in
            a combined view. The records will be removed from the review queue and won&apos;t be suggested for linking
            again.
          </Typography>
        </WarningBanner>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button variant='ghost' onClick={onCancel}>
          Cancel
        </Button>
        <Button variant='primary' onClick={onConfirm}>
          Don&apos;t link patients
        </Button>
      </DialogActions>
    </Dialog>
  )
}
